import React, { forwardRef } from 'react'
import clsx from 'clsx'

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'primary-hero' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success' | 'nav' | 'luxury'
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  fullWidth?: boolean
  loading?: boolean
  disabled?: boolean
  icon?: React.ReactNode
  iconRight?: React.ReactNode
  children?: React.ReactNode
}

// eslint-disable-next-line react-refresh/only-export-components
export const buildButtonClasses = (options?: Partial<ButtonProps>): string => {
  const { variant = 'primary', size = 'md', fullWidth, loading } = options || {}

  const baseClasses = clsx(
    'inline-flex items-center justify-center font-semibold rounded-brand',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--focus-ring)]',
    'transition-all duration-300',
    'disabled:opacity-60 disabled:cursor-not-allowed',
    loading && 'relative text-transparent pointer-events-none'
  )

  const variants = {
    // Primary: Metallic gold gradient matching login/luxury buttons
    primary: clsx(
      '[background-image:var(--gradient-gold-button)]',
      'text-[var(--text-on-accent)] font-semibold',
      'shadow-[var(--shadow-button-primary)]',
      'hover:shadow-[var(--shadow-lg),var(--shadow-glow)] hover:translate-y-[-1px] hover:brightness-110',
      'active:translate-y-[1px] active:scale-[0.98]'
    ),
    // Hero CTA: Larger glow for high-visibility actions
    'primary-hero': clsx(
      '[background-image:var(--gradient-gold-button)]',
      'text-[var(--text-on-accent)] font-semibold',
      'shadow-[var(--shadow-button-primary)]',
      'hover:shadow-[var(--shadow-glow-strong)] hover:translate-y-[-2px] hover:brightness-110',
      'active:translate-y-[1px] active:scale-[0.98]'
    ),
    // Secondary: Glass effect with gold accent border
    secondary: clsx(
      'bg-[rgba(255,255,255,0.03)] backdrop-blur-sm',
      'text-[var(--text-primary)]',
      'border border-[var(--border-accent)]',
      'hover:bg-[rgba(var(--accent-glow-rgba),0.08)] hover:border-[var(--accent)]'
    ),
    outline: clsx(
      'border border-[var(--border)]',
      'bg-transparent text-[var(--text-primary)]',
      'hover:bg-[var(--bg-hover)] hover:border-[var(--border-accent)]'
    ),
    ghost: clsx(
      'bg-transparent text-[var(--text-secondary)]',
      'hover:bg-[var(--bg-hover)] hover:text-[var(--accent)]'
    ),
    danger: clsx(
      'bg-[var(--state-danger)] text-white',
      'hover:opacity-90',
      'active:translate-y-[1px]'
    ),
    success: clsx(
      'bg-[var(--state-success)] text-white',
      'hover:opacity-90',
      'active:translate-y-[1px]'
    ),
    nav: clsx(
      'bg-transparent text-[var(--text-secondary)]',
      'hover:bg-[var(--bg-hover)] hover:text-[var(--accent)]'
    ),
    // Luxury: Gold gradient with clipped polygon shape
    luxury: clsx(
      'font-[var(--font-family-ui)] font-semibold tracking-[0.01em]',
      'text-[var(--text-on-accent)]',
      'hover:translate-y-[-2px] hover:brightness-110',
      'active:translate-y-[1px] active:scale-[0.98]'
    ),
  }

  const sizes = {
    xs: 'text-xs px-2 py-1 gap-1',
    sm: 'text-sm px-3 py-1.5 gap-1.5',
    md: 'text-sm px-4 py-2 gap-2',
    lg: 'text-base px-5 py-2.5 gap-2.5',
    xl: 'text-base px-6 py-3 gap-3',
  }

  return clsx(baseClasses, variants[variant], sizes[size], fullWidth && 'w-full')
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      fullWidth,
      loading,
      disabled,
      icon,
      iconRight,
      children,
      className,
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        className={clsx(buildButtonClasses({ variant, size, fullWidth, loading, disabled }), className)}
        disabled={disabled || loading}
        {...props}
      >
        {/* Loading spinner overlay */}
        {loading && (
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent" />
          </span>
        )}

        {/* Content */}
        {!loading && (
          <>
            {icon && <span className="flex-shrink-0 text-inherit">{icon}</span>}
            {children && <span className="text-inherit">{children}</span>}
            {iconRight && <span className="flex-shrink-0 text-inherit">{iconRight}</span>}
          </>
        )}
      </button>
    )
  }
)

Button.displayName = 'Button'
